import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Field } from "@/pages/admin/Field";
import { FileOrUrlField } from "@/components/common/FileOrUrlField";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Pin, BarChart3, Mail } from "lucide-react";

const TYPES = [["gr", "Government GR"], ["notice", "Notification"], ["circular", "Association circular"], ["legal", "Legal order"], ["training", "Training / CME"]];
const DEPTS = ["Ayush Directorate", "Health and Family Welfare", "Finance", "General Administration", "GMOAA", "Other"];
const empty = {
  type: "gr", category: "ayush", title_en: "", title_gu: "", number: "", date: new Date().toISOString().slice(0, 10),
  department: "Ayush Directorate", pdf_url: "", official_link: "", summary_en: "", summary_gu: "",
  visibility: "public", districts: "all", show_home: true, pinned: false, show_new: true,
  remove_home_date: "", status: "published", allow_download: true,
};

export const GrsSection = () => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const load = () => adminApi.adminGrs().then(setRows);
  useEffect(() => { load(); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (g) => { setForm({ ...empty, ...g, districts: Array.isArray(g.districts) ? g.districts.join(",") : (g.districts || "all") }); setEditId(g.id); setOpen(true); };

  const save = async () => {
    if (!form.title_en || !form.number) { toast.error("Title (English) and number are required"); return; }
    const payload = { ...form, districts: (form.districts === "all" || !form.districts) ? "all" : String(form.districts).split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editId) await adminApi.updateGr(editId, payload);
      else await adminApi.createGr(payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this document?")) return; await adminApi.deleteGr(id); toast.success("Deleted"); load(); };
  const unpublish = async (g) => { await adminApi.updateGr(g.id, { ...g, status: "draft" }); toast.success("Unpublished"); load(); };
  const showAnalytics = async (g) => { setAnalytics({ gr: g, data: null }); setAnalytics({ gr: g, data: await adminApi.grAnalytics(g.id) }); };
  const remind = async (g) => { const r = await adminApi.grRemind(g.id); toast.success(`Reminder emailed to ${r.sent} member(s)`); };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-gmoaa-green">GRs & Notifications</h2>
        <Button data-testid="gr-add-btn" onClick={openNew} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover"><Plus size={16} className="mr-1" /> Add</Button>
      </div>

      <div className="space-y-2">
        {rows.map((g) => (
          <div key={g.id} className="flex items-center justify-between rounded-xl border border-gmoaa-border-subtle bg-white p-3">
            <div className="min-w-0">
              <p className="flex items-center gap-2 font-medium text-gmoaa-text-dark">
                {g.pinned && <Pin size={13} className="text-gmoaa-red" />}
                <span className="rounded bg-gmoaa-green-light px-1.5 py-0.5 text-[10px] uppercase text-gmoaa-green">{g.type}</span>
                {g.status === "draft" && <span className="rounded bg-gmoaa-saffron-light px-1.5 py-0.5 text-[10px] text-gmoaa-saffron-hover">Draft</span>}
                {g.visibility === "members" && <span className="rounded bg-gmoaa-border-subtle px-1.5 py-0.5 text-[10px]">Members only</span>}
                <span className="truncate">{g.title_en}</span>
              </p>
              <p className="text-xs text-gmoaa-text-muted">{g.number} · {g.date} · {g.department || g.category} · {g.views || 0} opens · {g.downloads || 0} downloads</p>
            </div>
            <div className="flex shrink-0 gap-1">
              <Button size="sm" variant="ghost" onClick={() => showAnalytics(g)} data-testid={`gr-analytics-${g.id}`}><BarChart3 size={15} /></Button>
              <Button size="sm" variant="ghost" onClick={() => openEdit(g)}><Pencil size={15} /></Button>
              <Button size="sm" variant="ghost" className="text-gmoaa-red" onClick={() => del(g.id)}><Trash2 size={15} /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto bg-gmoaa-cream">
          <DialogHeader><DialogTitle className="text-gmoaa-green">{editId ? "Edit" : "Add"} document</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <p className="mb-1 text-xs font-medium text-gmoaa-text-muted">Type</p>
              <Select value={form.type} onValueChange={set("type")}><SelectTrigger className="border-gmoaa-border-subtle bg-white" data-testid="gr-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t[0]} value={t[0]}>{t[1]}</SelectItem>)}</SelectContent></Select>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-gmoaa-text-muted">Department</p>
              <Select value={form.department} onValueChange={set("department")}><SelectTrigger className="border-gmoaa-border-subtle bg-white" data-testid="gr-dept"><SelectValue /></SelectTrigger>
                <SelectContent>{DEPTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="sm:col-span-2"><Field label="Title (English)" value={form.title_en} onChange={set("title_en")} testid="gr-title-en" /></div>
            <div className="sm:col-span-2"><Field label="Title (Gujarati)" value={form.title_gu} onChange={set("title_gu")} /></div>
            <Field label="GR / Notification number" value={form.number} onChange={set("number")} testid="gr-number" />
            <Field label="Date issued" type="date" value={form.date} onChange={set("date")} />
            <div className="sm:col-span-2"><FileOrUrlField label="PDF document (upload, PDF only, max 25 MB)" accept="pdf" value={form.pdf_url} onChange={set("pdf_url")} testid="gr-pdf" /></div>
            <div className="sm:col-span-2"><Field label="Official link (optional)" value={form.official_link} onChange={set("official_link")} /></div>
            <div className="sm:col-span-2"><Field label="Short summary (optional)" textarea value={form.summary_en} onChange={set("summary_en")} /></div>
            <div>
              <p className="mb-1 text-xs font-medium text-gmoaa-text-muted">Visibility</p>
              <Select value={form.visibility} onValueChange={set("visibility")}><SelectTrigger className="border-gmoaa-border-subtle bg-white" data-testid="gr-visibility"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="public">Public</SelectItem><SelectItem value="members">Members only</SelectItem></SelectContent></Select>
            </div>
            <Field label="Districts (blank = all; else comma slugs)" value={form.districts === "all" ? "" : form.districts} onChange={(v) => set("districts")(v || "all")} />
            <Field label="Remove from home on (optional)" type="date" value={form.remove_home_date} onChange={set("remove_home_date")} />
            <div>
              <p className="mb-1 text-xs font-medium text-gmoaa-text-muted">Status</p>
              <Select value={form.status} onValueChange={set("status")}><SelectTrigger className="border-gmoaa-border-subtle bg-white" data-testid="gr-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="published">Published</SelectItem><SelectItem value="draft">Draft</SelectItem></SelectContent></Select>
            </div>
            <div className="flex flex-wrap gap-4 sm:col-span-2">
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.show_home} onCheckedChange={set("show_home")} /> Show on home</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.pinned} onCheckedChange={set("pinned")} data-testid="gr-pinned" /> Pin as IMPORTANT (red bar)</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.show_new} onCheckedChange={set("show_new")} /> NEW tag until opened</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.allow_download !== false} onCheckedChange={set("allow_download")} data-testid="gr-allow-download" /> Allow download (off = view only)</label>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="gr-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!analytics} onOpenChange={(o) => !o && setAnalytics(null)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto bg-gmoaa-cream">
          <DialogHeader><DialogTitle className="text-gmoaa-green">Document analytics</DialogTitle></DialogHeader>
          {analytics?.data ? (
            <div className="space-y-3">
              <p className="text-sm font-medium">{analytics.gr.title_en}</p>
              <div className="flex gap-4 text-sm">
                <span>Opened: <b>{analytics.data.opens}</b> / {analytics.data.audience}</span>
                <span>Downloads: <b>{analytics.data.downloads}</b></span>
              </div>
              <Button size="sm" onClick={() => remind(analytics.gr)} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover" data-testid="gr-remind"><Mail size={14} className="mr-1" /> Email reminder to those who haven't opened</Button>
              <div>
                <p className="mb-1 text-xs font-semibold text-gmoaa-text-muted">Not opened yet ({analytics.data.not_opened.length})</p>
                <div className="max-h-56 space-y-1 overflow-y-auto">
                  {analytics.data.not_opened.map((m) => <div key={m.id} className="rounded border border-gmoaa-border-subtle bg-white px-2 py-1 text-xs">{m.name_en} · {m.district}</div>)}
                </div>
              </div>
            </div>
          ) : <p className="text-sm text-gmoaa-text-muted">Loading…</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
};
